/**
 * THE PROJECT DECISION LOG — 3.19 Learning & Governance spine, D1 (pure layer).
 *
 * Decisions MADE: what / when / who / why + what they changed. This module is the
 * pure core — the `Decision` shape, its status rules, the supersede chain, and the
 * DERIVE-FIRST composers that turn acts the PM already does (clearing a decision
 * blocker, settling a sign-off, promoting a change-request assumption) into a draft
 * decision, so the log composes from the board rather than a blank retro form. No
 * store, no database: the store (createDecision/decideDecision/supersedeDecision/
 * withdrawDecision) persists what these produce, and the table (migration 0171) is
 * the record.
 *
 * SUPERSEDE, NEVER EDIT (ADR practice): a decided decision is never rewritten — a
 * change is a NEW decision whose `supersedes` names the old one, and the old one
 * flips to 'superseded'. Only a still-'open' draft may be edited in place.
 */

import { richFromText, type RichDoc } from './rich-text';

export type DecisionStatus = 'open' | 'decided' | 'superseded' | 'withdrawn';
export type DecisionOrigin = 'manual' | 'blocker' | 'signoff' | 'change-request';

/** A Reveal deep-link target the decision affected — the card/scope/budget/CR, so
 *  the answer sits with the thing. `kind` matches the app's Reveal targets. */
export interface DecisionLink {
  kind: string;
  id: string;
  label: string;
}

/** One decision as read back from the `decisions` table (migration 0171). */
export interface Decision {
  id: string;
  title: string;
  /** Why, and what it changed — the reasoning a future reader needs, as rich text
   *  (a rationale can run to several paragraphs). Absent when none was written. */
  detail?: RichDoc;
  status: DecisionStatus;
  /** When it moved to 'decided' (ms); absent while 'open'. */
  decidedAt?: number;
  /** Who decided it (email), resolved to a name on screen. */
  decidedBy?: string;
  /** Who logged it (email). */
  madeBy: string;
  /** The decision this one replaces (ADR supersede); the old row is 'superseded'. */
  supersedes?: string;
  /** What seeded it, so the log composes from the board. */
  origin: DecisionOrigin;
  /** The source's id/ref (the blocker's target, the sign-off id, the CR id). */
  originRef?: string;
  /** Why it was withdrawn — captured in the app when a decision is withdrawn (the
   *  withdraw prompt requires it), kept with the decision and shown on the Decisions
   *  screen. Absent unless `status` is 'withdrawn', or for a decision withdrawn
   *  before this was captured (migration 0180). */
  withdrawnReason?: string;
  links?: DecisionLink[];
  position?: number;
  createdAt: number;
  updatedAt?: number;
}

export const DECISION_STATUS_LABELS: Record<DecisionStatus, string> = {
  open: 'Open',
  decided: 'Decided',
  superseded: 'Superseded',
  withdrawn: 'Withdrawn',
};

export const DECISION_ORIGIN_LABELS: Record<DecisionOrigin, string> = {
  manual: 'Logged by hand',
  blocker: 'From a cleared blocker',
  signoff: 'From a sign-off',
  'change-request': 'From a change request',
};

/** A decision still counts toward the live log unless it has been superseded or
 *  withdrawn — those stay in the record but drop out of the "current decisions" read. */
export const isLiveDecision = (d: Pick<Decision, 'status'>): boolean =>
  d.status === 'open' || d.status === 'decided';

/** An OPEN draft may still be edited in place; everything else is supersede-only. */
export const canEditInPlace = (d: Pick<Decision, 'status'>): boolean => d.status === 'open';

/** Only an open decision can be marked decided. */
export const canDecide = (d: Pick<Decision, 'status'>): boolean => d.status === 'open';

/** An open or decided decision can be withdrawn; a superseded/withdrawn one cannot. */
export const canWithdraw = (d: Pick<Decision, 'status'>): boolean =>
  d.status === 'open' || d.status === 'decided';

/** Only a decided (live) decision can be superseded — you do not supersede a draft
 *  (edit it) nor one already superseded/withdrawn. */
export const canSupersede = (d: Pick<Decision, 'status'>): boolean => d.status === 'decided';

/**
 * THE LIVE LOG — newest first, with superseded and withdrawn decisions dropped. A
 * decision that has been superseded by another in the set is live-filtered too, even
 * if (through a crossed write) its own status has not yet flipped: the presence of a
 * newer row naming it via `supersedes` is authoritative.
 */
export function liveDecisions(all: Decision[]): Decision[] {
  const supersededIds = new Set(all.map((d) => d.supersedes).filter(Boolean) as string[]);
  return all
    .filter((d) => isLiveDecision(d) && !supersededIds.has(d.id))
    .sort((a, b) => b.createdAt - a.createdAt);
}

/** The full supersede chain behind a decision, oldest first — follow `supersedes`
 *  back through the set. Used to show "this replaced …" history. Guards against a
 *  cycle so a crossed write can never loop forever. */
export function supersedeChain(d: Decision, all: Decision[]): Decision[] {
  const byId = new Map(all.map((x) => [x.id, x]));
  const chain: Decision[] = [];
  const seen = new Set<string>([d.id]);
  let cur = d.supersedes ? byId.get(d.supersedes) : undefined;
  while (cur && !seen.has(cur.id)) {
    chain.unshift(cur);
    seen.add(cur.id);
    cur = cur.supersedes ? byId.get(cur.supersedes) : undefined;
  }
  return chain;
}

/** A draft decision — what a composer or the add form hands the store to persist.
 *  `madeBy`, `createdAt` and the id are stamped by the store. */
export interface DecisionDraft {
  title: string;
  detail?: RichDoc;
  status?: Extract<DecisionStatus, 'open' | 'decided'>;
  /** Who made the call (email). A log is often written after the fact, for a
   *  decision someone else made — so this may differ from who typed it. Absent
   *  defaults to the signed-in person. Only meaningful when `status` is 'decided'. */
  decidedBy?: string;
  /** When the call was made (ms). Absent defaults to now; lets a past decision be
   *  recorded with its real date. Only meaningful when `status` is 'decided'. */
  decidedAt?: number;
  origin: DecisionOrigin;
  originRef?: string;
  links?: DecisionLink[];
}

/**
 * IS THIS DRAFT ALREADY IN THE LOG? A LIVE decision from the same source (origin +
 * ref) with the same title means this sign-off / CR assumption was logged already —
 * so the derive-first affordance shows "In the log" instead of logging a duplicate.
 * Keyed on the title too, so sibling assumptions on ONE change request stay
 * independent. It reads the log, not a per-host flag, so the guard survives a
 * reload and a second click never duplicates. A draft with no `originRef` (the
 * manual log form) is never "already logged".
 */
export function alreadyLogged(
  decisions: Decision[],
  draft: Pick<DecisionDraft, 'origin' | 'originRef' | 'title'>,
): boolean {
  if (!draft.originRef) return false;
  const title = draft.title.trim();
  return decisions.some(
    (d) =>
      isLiveDecision(d) &&
      d.origin === draft.origin &&
      d.originRef === draft.originRef &&
      d.title === title,
  );
}

/* ── DERIVE-FIRST composers — compose a draft from what the board already holds ── */

/** From a cleared `decision`-type blocker on a card: the blocker was "we must decide
 *  X"; clearing it asks "what was decided?". Links back to the card. The title is
 *  left for the person to complete from the blocker's own text. */
export function decisionFromBlocker(src: {
  issueId: string;
  issueRef: string;
  issueTitle: string;
  blockerText?: string;
}): DecisionDraft {
  const what = (src.blockerText ?? '').trim();
  return {
    title: what || `Decision on ${src.issueRef}`,
    detail: what ? richFromText(`Resolving the blocker on ${src.issueRef} — ${src.issueTitle}.`) : undefined,
    status: 'decided',
    origin: 'blocker',
    originRef: src.issueId,
    links: [{ kind: 'issue', id: src.issueId, label: `${src.issueRef} ${src.issueTitle}`.trim() }],
  };
}

/** The BOARD's "do this next" clear removes a blocker from EVERY card waiting on
 *  it at once, so a decision cleared there is ONE call linked to all of them — not
 *  one per card. Same shape as decisionFromBlocker (origin 'blocker'): the first
 *  card is the originRef, with a link per affected card. */
export function decisionFromBlockerGroup(
  issues: { id: string; ref: string; title: string }[],
  blockerText?: string
): DecisionDraft {
  const head = issues[0];
  const what = (blockerText ?? '').trim();
  const refs = issues.map((i) => i.ref).join(', ');
  return {
    title: what || `Decision on ${refs}`,
    detail: what
      ? richFromText(
          issues.length === 1
            ? `Resolving the blocker on ${head.ref} — ${head.title}.`
            : `Resolving the blocker on ${refs}.`
        )
      : undefined,
    status: 'decided',
    origin: 'blocker',
    originRef: head.id,
    links: issues.map((i) => ({ kind: 'issue', id: i.id, label: `${i.ref} ${i.title}`.trim() })),
  };
}

/** From a settled sign-off: the approval IS the decision. Links to the sign-off and
 *  carries its reason as the why, rather than duplicating it. */
export function decisionFromSignoff(src: {
  signoffId: string;
  title: string;
  why?: string;
  kindLabel?: string;
}): DecisionDraft {
  return {
    title: src.title.trim() || 'Sign-off decision',
    detail: (src.why ?? '').trim() ? richFromText(src.why) : undefined,
    status: 'decided',
    origin: 'signoff',
    originRef: src.signoffId,
    links: [{ kind: 'signoff', id: src.signoffId, label: src.kindLabel || 'Sign-off' }],
  };
}

/** From a change request's assumption that carries a decision: promote it to the log.
 *  Links to the CR so the scope answer sits with the request. */
export function decisionFromChangeRequest(src: {
  crId: string;
  crRef: string;
  assumptionText: string;
  decisionText: string;
}): DecisionDraft {
  return {
    title: src.decisionText.trim() || `Scope decision on ${src.crRef}`,
    detail: src.assumptionText.trim()
      ? richFromText(`Assumption on ${src.crRef}: ${src.assumptionText.trim()}`)
      : undefined,
    status: 'decided',
    origin: 'change-request',
    originRef: src.crId,
    links: [{ kind: 'change-request', id: src.crId, label: src.crRef }],
  };
}
